/**
 * Athletics-hub shared read contract — common shapes.
 *
 * The net-new tiered read contract both the web school-admin prototype and the
 * mobile Athletics destination consume (plan 2026-06-18-004, U1). Games reuse
 * the source-agnostic `SchoolEventSchema`; per-game scores reuse
 * `AthleticOpponentSchema`. No Blackbaud-specific fields (`directions`,
 * internal `metadata`) cross this wire — the schemas are `.strict()` so a
 * response carrying them FAILS validation (KTD4, wire-exclusion is
 * contract-enforced).
 *
 * Design decisions:
 * - Scores are PER-GAME only; there is no aggregate win-loss `record` field
 *   (KTD5). Per-game scores ride on the reused `SchoolEvent.opponents`.
 * - Public coach identity is NAME + ROLE only — never email, photo, or contact
 *   (KTD3, FERPA / directory PII boundary).
 * - Null-sport sections group under a reserved "Other" bucket key (KTD7).
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-004-feat-athletics-hub-front-door-plan.md (U1)
 */

import { z } from 'zod'

/**
 * Reserved sport-group key for sections whose `sportName` is null (the
 * sport-label join deliberately misses — KTD7). Clients render this bucket
 * under an "Other / Unsorted" heading with stable ordering.
 */
export const ATHLETICS_HUB_OTHER_SPORT_KEY = 'other'

/**
 * Athletic gender code as stored on `Section.gender` and carried on the wire:
 * `M` (Boys) / `F` (Girls) / `C` (Co-Ed). The backend write-side normalizes BB's
 * "Boys"/"Girls"/"Co-Ed"/empty to these codes (SCH-244) and the read-side
 * re-validates, so any value reaching this contract is one of the three (or null
 * on the field). Clients map to display labels via `genderLabel` (utils).
 */
export const GenderCodeSchema = z.enum(['M', 'F', 'C'])
export type GenderCode = z.infer<typeof GenderCodeSchema>

/**
 * Public coach identity on a team — name + role ONLY. Never carries email,
 * photo, or any contact field (KTD3). `.strict()` so a projector accidentally
 * widening this shape fails the contract test rather than leaking PII.
 */
export const AthleticsHubCoachSchema = z
  .object({
    /** Display name as synced. */
    name: z.string(),
    /** Coaching role label (e.g. "Head Coach"). Free-text from the source. */
    role: z.string().nullable().optional()
  })
  .strict()
export type AthleticsHubCoach = z.infer<typeof AthleticsHubCoachSchema>

/**
 * One grid item — a single current-season ATHLETICS section presented as a
 * browsable team card. `sectionPublicId` is the public CUID the team-detail
 * endpoint takes as `:sectionPublicId`; never the internal DB bigint.
 */
export const AthleticsHubGridItemSchema = z
  .object({
    /** Public CUID of the section — the team-detail route param. */
    section_public_id: z.string(),
    /** Team / section display name. */
    name: z.string(),
    /**
     * Sport label joined from `Section.sportName`. Null when the label join
     * misses; such sections land in the "Other" group (KTD7).
     */
    sport_name: z.string().nullable().optional(),
    /**
     * School DIVISION label sourced from `Section.platformSchoolLevelName`
     * (e.g. "Upper School", "Middle School") — NOT the athletic level-of-play.
     * The Varsity/JV/MS athletic axis is the separate `level_of_play` field
     * below. Client-side filter only.
     */
    level: z.string().nullable().optional(),
    /**
     * Normalized base-sport grouping key (`Section.normalizedSport`) — folds
     * BB's fragmented raw `sport_name` ("Soccer - Boys", "Soccer - Girls - JV")
     * into one stable sport (SCH-244). Null on unbackfilled schools/sections;
     * clients fall back to `sport_name`/the "Other" bucket. Additive + optional.
     */
    normalized_sport: z.string().nullable().optional(),
    /**
     * Athletic gender token (`Section.gender`): `M`/`F`/`C`, parsed from the
     * team name + validated against the sport's sportslevels vocabulary. Null
     * when unparseable/unvalidated (degrade, not guess). Client-side filter only.
     */
    gender: GenderCodeSchema.nullable().optional(),
    /**
     * Athletic level-of-play (`Section.levelOfPlay`): Varsity/JV/MS/… — the
     * canonical athletic level, distinct from the school `level` (division)
     * above. Null when unparseable/unvalidated. Client-side filter only.
     */
    level_of_play: z.string().nullable().optional(),
    /**
     * Sport SEASON token (`Section.sportSeason`): a STRING — Fall/Winter/Spring
     * — joined by `sport.id`. Named `sport_season` (NOT `season`) to avoid
     * colliding with the team-detail `season` OBJECT (`{start,end}` game-window).
     * Null when no season join. Client-side filter only.
     */
    sport_season: z.string().nullable().optional()
  })
  .strict()
export type AthleticsHubGridItem = z.infer<typeof AthleticsHubGridItemSchema>

/**
 * A sport-grouped bucket of grid items. `sport_key` is the stable grouping key:
 * the sport label for real sports, or `ATHLETICS_HUB_OTHER_SPORT_KEY` for the
 * reserved null-sport bucket.
 */
export const AthleticsHubSportGroupSchema = z
  .object({
    /** Stable grouping key — a sport label, or the reserved "other" key. */
    sport_key: z.string(),
    /** Human-readable group heading (null for the "Other" bucket). */
    sport_name: z.string().nullable().optional(),
    /** Teams in this sport, in display order. */
    teams: z.array(AthleticsHubGridItemSchema)
  })
  .strict()
export type AthleticsHubSportGroup = z.infer<typeof AthleticsHubSportGroupSchema>

/**
 * Team summary header — the public-leaning identity block on the team-detail
 * payload. Carries no roster, no PII beyond coach name + role.
 */
export const AthleticsHubTeamSummarySchema = z
  .object({
    /** Public CUID of the section. */
    section_public_id: z.string(),
    /** Team / section display name. */
    name: z.string(),
    /** Sport label, null when unsorted. */
    sport_name: z.string().nullable().optional(),
    /**
     * School DIVISION label (`Section.platformSchoolLevelName`, e.g. "Upper
     * School") — NOT the athletic level-of-play (that is `level_of_play` below).
     */
    level: z.string().nullable().optional(),
    /**
     * Normalized base-sport grouping key (`Section.normalizedSport`). Null on
     * unbackfilled schools/sections. Additive + optional (SCH-244).
     */
    normalized_sport: z.string().nullable().optional(),
    /** Athletic gender token (`Section.gender`): `M`/`F`/`C`. Null when unvalidated. */
    gender: GenderCodeSchema.nullable().optional(),
    /**
     * Athletic level-of-play (`Section.levelOfPlay`): Varsity/JV/MS/… — distinct
     * from the school `level` (division) above. Null when unvalidated.
     */
    level_of_play: z.string().nullable().optional(),
    /**
     * Sport SEASON token (`Section.sportSeason`): a STRING (Fall/Winter/Spring).
     * Named `sport_season` (NOT `season`) — the team-detail `season` is a
     * separate OBJECT game-window. Null when no season join.
     */
    sport_season: z.string().nullable().optional(),
    /** Public coaching staff — name + role only (KTD3). */
    coaches: z.array(AthleticsHubCoachSchema)
  })
  .strict()
export type AthleticsHubTeamSummary = z.infer<typeof AthleticsHubTeamSummarySchema>
